import { useMemo, useState, type FormEvent } from 'react';
import {
  fieldKey, parseCsv, DIALLER_FIELD_TYPES,
  type DiallerField, type DiallerFieldType, type DiallerImportResult, type DiallerList,
} from '@arcnet/contracts';
import {
  useCreateApiClient, useDeleteDiallerList, useDeleteDiallerRecord, useDiallerApiClients, useDiallerLists,
  useDiallerRecords, useImportSlice, useRevokeApiClient, useSaveDiallerList, useUpdateApiClient,
} from '../../lib/api/hooks-dialler.js';
import { leadSnippet } from './apiSnippet.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { useSubroute } from '../../lib/subroute.js';
import { formatNumber } from '../../lib/format.js';
import { Badge, Empty, ErrorBanner, SectionHead, SubHead, TableSkeleton, Tab, Tabs } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * Lead lists (docs/36 D1b, owner 25 September 2026): the people a campaign will call, from a
 * spreadsheet or one at a time from the client's own systems through the public API (docs/38).
 *
 * The client's alone -- nobody above the account can read a lead (0123) -- so this page is only
 * ever seen by the account's own people.
 *
 * A list is opened by its name in the address: /dialler/lists/renewals-march.
 */

const slugOf = (name: string): string =>
  name.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'list';

function slugs(lists: readonly DiallerList[]): Map<string, string> {
  const out = new Map<string, string>();
  const taken = new Map<string, number>();
  for (const l of [...lists].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
    const base = slugOf(l.name);
    const n = (taken.get(base) ?? 0) + 1;
    taken.set(base, n);
    out.set(l.id, n === 1 ? base : `${base}-${n}`);
  }
  return out;
}

export function DiallerListsPage({ tenantId }: { tenantId: string }) {
  const route = useSubroute();
  const lists = useDiallerLists(tenantId);
  const can = useSession((s) => s.can);
  const [adding, setAdding] = useState(false);
  const bySlug = slugs(lists.data?.data ?? []);
  const open = route.rest[0];
  const list = open ? (lists.data?.data ?? []).find((l) => bySlug.get(l.id) === open) ?? null : null;

  if (open) {
    return (
      <>
        <button type="button" className="arc-crumb" onClick={() => route.go([])}
          style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
          <Icon name="arrowLeft" size={15} /> Lead lists
        </button>
        {lists.data && !list && (
          <div className="arc-banner arc-banner-warn"><Icon name="warning" size={14} /><span>There is no list called {open}.</span></div>
        )}
        {list && <ListView tenantId={tenantId} list={list} onGone={() => route.go([])} />}
      </>
    );
  }

  return (
    <>
      <SectionHead title="Lead lists" sub="The people a campaign will call: from a spreadsheet, or one at a time from a form or an ad."
        action={can('dialler:write')
          ? <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setAdding((v) => !v)}>
              {adding ? 'Cancel' : <><Icon name="plus" size={13} /> List</>}
            </button>
          : undefined} />
      {adding && <NewList tenantId={tenantId} onMade={(name) => { setAdding(false); route.go([slugOf(name)]); }} />}
      {lists.error && <ErrorBanner error={lists.error} />}
      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>List</th><th className="arc-td-right">Leads</th><th className="arc-td-right">Do not call</th>
                <th className="arc-td-right">Own fields</th><th>Made</th></tr>
            </thead>
            {lists.data === undefined ? <TableSkeleton cols={5} /> : (
              <tbody>
                {lists.data.data.map((l) => (
                  <tr key={l.id} className="arc-clickable" onClick={() => route.go([bySlug.get(l.id)!])}>
                    <td>
                      <span style={{ fontWeight: 570 }}>{l.name}</span>
                      {l.description && <div className="arc-subtle" style={{ fontSize: 11.5 }}>{l.description}</div>}
                    </td>
                    <td className="arc-td-right arc-num">{formatNumber(l.records)}</td>
                    <td className="arc-td-right arc-num">{l.suppressed ? formatNumber(l.suppressed) : '—'}</td>
                    <td className="arc-td-right arc-num">{l.fields.length || '—'}</td>
                    <td className="arc-num">{new Date(l.created_at).toLocaleDateString('en-ZA')}</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {lists.data && lists.data.data.length === 0 && (
          <Empty icon="contacts" title="No lead lists" hint="Make a list, then import a spreadsheet into it or connect a system to it." />
        )}
      </div>
    </>
  );
}

function NewList({ tenantId, onMade }: { tenantId: string; onMade: (name: string) => void }) {
  const save = useSaveDiallerList(tenantId);
  const [name, setName] = useState('');
  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate({ name: name.trim(), fields: [] }, { onSuccess: () => onMade(name.trim()) }); }}>
      <div className="arc-row" style={{ gap: 10, alignItems: 'flex-end' }}>
        <div className="arc-field" style={{ margin: 0, flex: 1 }}>
          <label className="arc-label" htmlFor="dl-name">Name</label>
          <input id="dl-name" className="arc-input" value={name} maxLength={120} required
            onChange={(e) => setName(e.target.value)} placeholder="Funeral cover enquiries" />
        </div>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !name.trim()}>Make it</button>
      </div>
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}

// ================================================================================== one list

type ListTab = 'leads' | 'fields' | 'import' | 'sources';
const TABS: ReadonlyArray<readonly [ListTab, string]> = [
  ['leads', 'Leads'], ['fields', 'Fields'], ['import', 'Import a spreadsheet'], ['sources', 'API access'],
];

function ListView({ tenantId, list, onGone }: { tenantId: string; list: DiallerList; onGone: () => void }) {
  const route = useSubroute();
  const can = useSession((s) => s.can);
  const del = useDeleteDiallerList(tenantId);
  const tab = (TABS.find(([t]) => t === route.rest[1])?.[0]) ?? 'leads';
  const saveTries = useSaveDiallerList(tenantId);
  // How many tries a contact from this list gets in a campaign (0128): one try is one pass through
  // all its numbers.
  const setTries = (tries: number) => saveTries.mutate({
    id: list.id, name: list.name, description: list.description, tries,
    fields: list.fields.map((f) => ({ key: f.key, label: f.label, type: f.type })),
  });
  const go = (t: ListTab) => route.go([route.rest[0]!, ...(t === 'leads' ? [] : [t])]);

  return (
    <>
      <SectionHead title={list.name}
        sub={`${formatNumber(list.records)} lead${list.records === 1 ? '' : 's'}${list.suppressed ? `, ${formatNumber(list.suppressed)} on do-not-call` : ''}`}
        action={can('dialler:write') ? (
          <div className="arc-row" style={{ gap: 8 }}>
          <label className="arc-row" style={{ gap: 6, fontSize: 12.5 }} title="A try calls each of the contact's numbers in order. Not reached, and it is tried again later, up to this many times.">
            Tries per contact
            <select className="arc-select" style={{ width: 70 }} value={list.tries} disabled={saveTries.isPending}
              aria-label="Tries per contact" onChange={(e) => setTries(Number(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={del.isPending}
            onClick={() => {
              void confirmAction({ title: `Delete ${list.name} and its ${list.records} leads?`, body: 'Its API clients stop working at once. This cannot be undone.', action: 'Delete', danger: true }).then((ok) => { if (ok) {
                del.mutate(list.id, { onSuccess: onGone });
              } });
            }}>Delete list</button>
          </div>
        ) : undefined} />
      {saveTries.error && <ErrorBanner error={saveTries.error} />}
      <Tabs style={{ marginBottom: 14 }}>
        {TABS.map(([t, label]) => <Tab key={t} active={tab === t} onSelect={() => go(t)}>{label}</Tab>)}
      </Tabs>
      {tab === 'leads' && <Leads tenantId={tenantId} list={list} />}
      {tab === 'fields' && <Fields tenantId={tenantId} list={list} />}
      {tab === 'import' && <Import tenantId={tenantId} list={list} onDone={() => go('leads')} />}
      {tab === 'sources' && <Sources tenantId={tenantId} list={list} />}
    </>
  );
}

// ------------------------------------------------------------------------------------ leads

const STATE_LABEL: Record<string, string> = { new: 'waiting', suppressed: 'do not call', dialled: 'dialled', final: 'done' };

function Leads({ tenantId, list }: { tenantId: string; list: DiallerList }) {
  const can = useSession((s) => s.can);
  const [q, setQ] = useState('');
  const [state, setState] = useState('');
  const [offset, setOffset] = useState(0);
  const records = useDiallerRecords(tenantId, list.id, { q, state, offset });
  const del = useDeleteDiallerRecord(tenantId);
  const shown = list.fields.slice(0, 4);
  const total = records.data?.total ?? 0;

  return (
    <div className="arc-card">
      <div className="arc-toolbar">
        <div className="arc-search">
          <input className="arc-input" placeholder="Name, email or part of a number…" value={q}
            onChange={(e) => { setQ(e.target.value); setOffset(0); }} aria-label="Find a lead" />
        </div>
        <select className="arc-select" value={state} onChange={(e) => { setState(e.target.value); setOffset(0); }}
          aria-label="Which leads" style={{ width: 160 }}>
          <option value="">All</option>
          <option value="new">Waiting</option>
          <option value="suppressed">Do not call</option>
        </select>
        <div className="arc-spacer" />
        <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
          {total ? `${formatNumber(offset + 1)}–${formatNumber(Math.min(offset + 100, total))} of ${formatNumber(total)}` : ''}
        </span>
      </div>
      <div className="arc-table-wrap">
        <table className="arc-table">
          <thead>
            <tr>
              <th>Name</th><th>Number</th><th>Email</th>
              {shown.map((f) => <th key={f.key}>{f.label}</th>)}
              <th>From</th><th>State</th><th />
            </tr>
          </thead>
          {records.data === undefined ? <TableSkeleton cols={6 + shown.length} /> : (
            <tbody>
              {records.data.data.map((r) => (
                <tr key={r.id}>
                  <td>{[r.first_name, r.last_name].filter(Boolean).join(' ') || <span className="arc-subtle">—</span>}</td>
                  <td className="arc-mono">
                    +{r.phone}
                    {(r.phones ?? []).map((p) => <div key={p} className="arc-subtle" style={{ fontSize: 11.5 }}>+{p}</div>)}
                  </td>
                  <td>{r.email ?? <span className="arc-subtle">—</span>}</td>
                  {shown.map((f) => <td key={f.key}>{show(r.fields[f.key], f.type)}</td>)}
                  <td>
                    <span title={r.consent_page ?? undefined}>
                      {r.source}
                    </span>
                  </td>
                  <td><Badge tone={r.state === 'suppressed' ? 'warn' : 'muted'}>{STATE_LABEL[r.state] ?? r.state}</Badge></td>
                  <td className="arc-td-right">
                    {can('dialler:write') && (
                      <RowAction kind="delete" label="Delete lead"
                        onClick={() => { void confirmAction({ title: 'Delete this lead?', action: 'Delete', danger: true }).then((ok) => { if (ok) del.mutate(r.id); }); }} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      {records.data && total === 0 && (
        q || state
          ? <Empty icon="contacts" title="Nobody matches" />
          : <Empty icon="contacts" title="No leads yet" hint="Import a spreadsheet, or connect a system under API access." />
      )}
      {total > 100 && (
        <div className="arc-row" style={{ gap: 8, padding: 12 }}>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))}>Previous</button>
          <button type="button" className="arc-btn arc-btn-sm" disabled={offset + 100 >= total} onClick={() => setOffset(offset + 100)}>Next</button>
        </div>
      )}
    </div>
  );
}

function show(v: unknown, type: DiallerFieldType): string {
  if (v === null || v === undefined || v === '') return '—';
  if (type === 'yes_no') return v ? 'Yes' : 'No';
  if (type === 'date' && typeof v === 'string') return new Date(`${v}T00:00:00Z`).toLocaleDateString('en-ZA', { timeZone: 'UTC' });
  if (type === 'number' && typeof v === 'number') return v.toLocaleString('en-ZA');
  return String(v);
}

// ------------------------------------------------------------------------------------ fields

const TYPE_LABEL: Record<DiallerFieldType, string> = {
  text: 'Text', number: 'Number', date: 'Date', yes_no: 'Yes or no', email: 'Email',
};

function Fields({ tenantId, list }: { tenantId: string; list: DiallerList }) {
  const can = useSession((s) => s.can);
  const save = useSaveDiallerList(tenantId);
  const [rows, setRows] = useState(list.fields.map((f) => ({ key: f.key, label: f.label, type: f.type, fresh: false })));
  const changed = JSON.stringify(rows.map(({ fresh: _f, ...r }) => r)) !== JSON.stringify(list.fields.map((f) => ({ key: f.key, label: f.label, type: f.type })));

  return (
    <div className="arc-card">
      <div className="arc-card-body">
        <p className="arc-muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
          What this list knows about each person beyond their name, number and email: a renewal date, a
          premium, the product they asked about. A spreadsheet column or a form field lands in the field
          with the same key or label.
        </p>
        {rows.map((r, i) => (
          <div key={i} className="arc-row" style={{ gap: 8, marginBottom: 8, alignItems: 'flex-end' }}>
            <div className="arc-field" style={{ margin: 0, flex: 1 }}>
              {i === 0 && <label className="arc-label" htmlFor={`df-l-${i}`}>Label</label>}
              <input id={`df-l-${i}`} className="arc-input" value={r.label} maxLength={80} disabled={!can('dialler:write')}
                onChange={(e) => setRows((p) => p.map((x, n) => n === i
                  ? { ...x, label: e.target.value, key: x.fresh ? fieldKey(e.target.value) : x.key } : x))} />
            </div>
            <div className="arc-field" style={{ margin: 0, width: 200 }}>
              {i === 0 && <span className="arc-label">Key</span>}
              <code className="arc-mono arc-subtle" style={{ fontSize: 12 }}>{r.key || '—'}</code>
            </div>
            <div className="arc-field" style={{ margin: 0, width: 140 }}>
              {i === 0 && <label className="arc-label" htmlFor={`df-t-${i}`}>Type</label>}
              <select id={`df-t-${i}`} className="arc-select" value={r.type} disabled={!can('dialler:write')}
                onChange={(e) => setRows((p) => p.map((x, n) => n === i ? { ...x, type: e.target.value as DiallerFieldType } : x))}>
                {DIALLER_FIELD_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </select>
            </div>
            {can('dialler:write') && (
              <RowAction kind="delete" label="Remove field"
                onClick={() => setRows((p) => p.filter((_, n) => n !== i))} />
            )}
          </div>
        ))}
        {rows.length === 0 && <Empty icon="hash" title="No fields of its own yet" />}
        {can('dialler:write') && (
          <div className="arc-row" style={{ gap: 8, marginTop: 10 }}>
            <button type="button" className="arc-btn arc-btn-sm"
              onClick={() => setRows((p) => [...p, { key: '', label: '', type: 'text', fresh: true }])}>
              <Icon name="plus" size={13} /> Field
            </button>
            <div className="arc-spacer" />
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!changed || save.isPending}
              onClick={() => save.mutate({
                id: list.id, name: list.name, description: list.description, tries: list.tries,
                fields: rows.filter((r) => r.label.trim() && r.key).map(({ fresh: _f, ...r }) => r),
              })}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
        <p className="arc-hint" style={{ marginTop: 8 }}>
          Removing a field keeps its values on each lead, unread; putting the same key back brings them back.
        </p>
        {save.error && <ErrorBanner error={save.error} />}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------ import

type Target = 'ignore' | 'phone' | 'phone_2' | 'phone_3' | 'phone_4' | 'phone_5'
  | 'first_name' | 'last_name' | 'name' | 'email' | 'new' | `field:${string}`;
const MORE_PHONES = ['phone_2', 'phone_3', 'phone_4', 'phone_5'] as const;

const ALIASES: Record<string, Target> = {
  phone: 'phone', phone_number: 'phone', mobile: 'phone', mobile_number: 'phone', cell: 'phone', cellphone: 'phone',
  tel: 'phone', telephone: 'phone', contact_number: 'phone', number: 'phone',
  first_name: 'first_name', firstname: 'first_name', given_name: 'first_name',
  last_name: 'last_name', lastname: 'last_name', surname: 'last_name', family_name: 'last_name',
  name: 'name', full_name: 'name', fullname: 'name',
  email: 'email', email_address: 'email', e_mail: 'email',
  phone_2: 'phone_2', phone2: 'phone_2', second_phone: 'phone_2', alt_phone: 'phone_2', alternate_phone: 'phone_2',
  other_phone: 'phone_2', work_phone: 'phone_2', home_phone: 'phone_2', mobile_2: 'phone_2', mobile2: 'phone_2',
  phone_3: 'phone_3', phone3: 'phone_3', phone_4: 'phone_4', phone4: 'phone_4', phone_5: 'phone_5', phone5: 'phone_5',
};

/** A second phone-like column is the contact's next number, not a second first number. */
function numberPhones(targets: Target[]): Target[] {
  const taken = new Set<Target>();
  return targets.map((t) => {
    if (t !== 'phone' && !MORE_PHONES.includes(t as typeof MORE_PHONES[number])) return t;
    const order: Target[] = ['phone', ...MORE_PHONES];
    const from = order.indexOf(t);
    const free = order.slice(from).find((x) => !taken.has(x)) ?? order.find((x) => !taken.has(x));
    if (!free) return 'ignore';
    taken.add(free);
    return free;
  });
}

function guess(header: string, fields: readonly DiallerField[]): Target {
  const k = fieldKey(header);
  if (ALIASES[k]) return ALIASES[k]!;
  const f = fields.find((x) => x.key === k || fieldKey(x.label) === k);
  return f ? `field:${f.key}` : header.trim() ? 'new' : 'ignore';
}

/**
 * A spreadsheet into a list. Read here in the browser, its columns matched by hand (with a first
 * guess from the headers), and sent in slices of 500 rows: every row is judged by the API with the
 * same rules as a lead from a form, and the ones it refuses come back with their row number and why.
 */
function Import({ tenantId, list, onDone }: { tenantId: string; list: DiallerList; onDone: () => void }) {
  const saveList = useSaveDiallerList(tenantId);
  const slice = useImportSlice(tenantId, list.id);
  const [file, setFile] = useState<{ name: string; header: string[]; rows: string[][] } | null>(null);
  const [map, setMap] = useState<Target[]>([]);
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null);
  const [result, setResult] = useState<(Omit<DiallerImportResult, 'import_id' | 'rejected'> & { rejected: Array<{ row: number; reason: string }> }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  const read = async (f: File) => {
    setResult(null); setError(null);
    if (f.size > 20 * 1024 * 1024) { setError('That file is over 20 MB: split it, or save only the columns you need.'); return; }
    const all = parseCsv(await f.text()).filter((r) => r.some((c) => c.trim() !== ''));
    if (all.length < 2) { setError('The file needs a header row and at least one row under it.'); return; }
    const [header, ...rows] = all as [string[], ...string[][]];
    setFile({ name: f.name, header, rows });
    setMap(numberPhones(header.map((h) => guess(h, list.fields))));
  };

  const hasPhone = map.includes('phone');
  const preview = useMemo(() => file?.rows.slice(0, 5) ?? [], [file]);

  const run = async () => {
    if (!file) return;
    setError(null);
    try {
      // New fields first, named from their headers, so the rows have somewhere to land.
      const fresh = map.map((t, i) => (t === 'new' ? i : -1)).filter((i) => i >= 0);
      const keys = new Map<number, string>();
      if (fresh.length) {
        const existing = new Set(list.fields.map((f) => f.key));
        const added: Array<{ key: string; label: string; type: DiallerFieldType }> = [];
        for (const i of fresh) {
          let key = fieldKey(file.header[i] ?? `column_${i + 1}`);
          if (['phone', 'first_name', 'last_name', 'email', 'name'].includes(key)) key = `${key}_2`;
          let n = 2;
          while (existing.has(key)) key = `${fieldKey(file.header[i] ?? 'column')}_${n++}`;
          existing.add(key);
          keys.set(i, key);
          added.push({ key, label: (file.header[i] ?? key).trim().slice(0, 80) || key, type: 'text' });
        }
        await saveList.mutateAsync({
          id: list.id, name: list.name, description: list.description, tries: list.tries,
          fields: [...list.fields.map((f) => ({ key: f.key, label: f.label, type: f.type })), ...added],
        });
      }

      const rows = file.rows.map((cells) => {
        const r = { phone: '', phones: [] as string[], first_name: null as string | null, last_name: null as string | null, email: null as string | null, fields: {} as Record<string, string> };
        const more: Record<string, string> = {};
        map.forEach((t, i) => {
          const v = (cells[i] ?? '').trim();
          if (!v || t === 'ignore') return;
          if (t === 'phone') r.phone = v;
          else if (MORE_PHONES.includes(t as typeof MORE_PHONES[number])) more[t] = v;
          else if (t === 'first_name') r.first_name = v;
          else if (t === 'last_name') r.last_name = v;
          else if (t === 'email') r.email = v;
          else if (t === 'name') {
            const [f, ...rest] = v.split(/\s+/);
            r.first_name = r.first_name ?? f ?? null;
            r.last_name = r.last_name ?? (rest.length ? rest.join(' ') : null);
          } else if (t === 'new') r.fields[keys.get(i)!] = v;
          else r.fields[t.slice(6)] = v;
        });
        r.phones = MORE_PHONES.map((k) => more[k]).filter((x): x is string => !!x);
        return r;
      });

      const total = { added: 0, updated: 0, suppressed: 0, rejected_total: 0, rejected: [] as Array<{ row: number; reason: string }> };
      let importId: string | undefined;
      setProgress({ sent: 0, total: rows.length });
      for (let start = 0; start < rows.length; start += 500) {
        const r = await slice.mutateAsync({
          ...(importId ? { import_id: importId } : { file_name: file.name.slice(0, 200) }),
          rows: rows.slice(start, start + 500),
        });
        importId = r.import_id;
        total.added += r.added; total.updated += r.updated; total.suppressed += r.suppressed;
        total.rejected_total += r.rejected_total;
        // The file's own row numbers: the header is row 1.
        for (const x of r.rejected) if (total.rejected.length < 100) total.rejected.push({ row: start + x.row + 2, reason: x.reason });
        setProgress({ sent: Math.min(rows.length, start + 500), total: rows.length });
      }
      setResult(total);
      setFile(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The import stopped.');
    } finally {
      setProgress(null);
    }
  };

  const targets: Array<[Target, string]> = [
    ['ignore', 'Leave out'], ['phone', 'Phone number'], ['phone_2', 'Phone 2'], ['phone_3', 'Phone 3'],
    ['phone_4', 'Phone 4'], ['phone_5', 'Phone 5'], ['name', 'Full name'], ['first_name', 'First name'],
    ['last_name', 'Surname'], ['email', 'Email'],
    ...list.fields.map((f) => [`field:${f.key}` as Target, f.label] as [Target, string]),
    ['new', 'A new field'],
  ];

  return (
    <div className="arc-card">
      <div className="arc-card-body">
        {result && (
          <div style={{ marginBottom: 14 }}>
            <p style={{ fontSize: 13 }}>
              <strong>{formatNumber(result.added)}</strong> added, {formatNumber(result.updated)} already on the list and
              updated{result.suppressed ? `, ${formatNumber(result.suppressed)} on do-not-call (kept, never dialled)` : ''}
              {result.rejected_total ? `, ${formatNumber(result.rejected_total)} left out` : ''}.
            </p>
            {result.rejected.length > 0 && (
              <div className="arc-card-body" style={{ background: 'var(--arc-surface-2)', borderRadius: 'var(--arc-r)', maxHeight: 220, overflowY: 'auto' }}>
                {result.rejected.map((x) => (
                  <div key={x.row} style={{ fontSize: 12 }}><span className="arc-mono">Row {x.row}</span>: {x.reason}</div>
                ))}
                {result.rejected_total > result.rejected.length && (
                  <div className="arc-subtle" style={{ fontSize: 12 }}>and {result.rejected_total - result.rejected.length} more</div>
                )}
              </div>
            )}
            <button type="button" className="arc-btn arc-btn-sm" style={{ marginTop: 10 }} onClick={onDone}>See the leads</button>
          </div>
        )}

        {!file && (
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="di-file">A CSV file</label>
            <input id="di-file" type="file" accept=".csv,text/csv" className="arc-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); e.target.value = ''; }} />
            <div className="arc-hint">
              From Excel or Google Sheets: File, Save as (or Download), CSV. The first row names the columns.
              The same number twice is one lead; a number already on the list is updated, not added again.
            </div>
          </div>
        )}

        {file && (
          <>
            <p style={{ fontSize: 13, marginBottom: 10 }}>
              <strong>{file.name}</strong>: {formatNumber(file.rows.length)} row{file.rows.length === 1 ? '' : 's'}. Say where each column goes.
            </p>
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead><tr><th>Column</th><th>Goes to</th><th>First rows</th></tr></thead>
                <tbody>
                  {file.header.map((h, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 550 }}>{h || <span className="arc-subtle">(no name)</span>}</td>
                      <td style={{ width: 220 }}>
                        <select className="arc-select" value={map[i]} aria-label={`Where ${h} goes`}
                          onChange={(e) => setMap((p) => p.map((t, n) => (n === i ? e.target.value as Target : t)))}>
                          {targets.map(([t, l]) => <option key={t} value={t}>{l}</option>)}
                        </select>
                      </td>
                      <td className="arc-subtle" style={{ fontSize: 12 }}>
                        {preview.map((r) => r[i]).filter(Boolean).slice(0, 3).join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!hasPhone && (
              <div className="arc-banner arc-banner-warn" style={{ marginTop: 10 }}>
                <Icon name="warning" size={14} /><span>Choose the column with the phone number: a lead without one cannot be called.</span>
              </div>
            )}
            {map.includes('new') && (
              <p className="arc-hint" style={{ marginTop: 8 }}>
                Columns going to a new field become text fields named after their headers; change a field’s type afterwards under Fields.
              </p>
            )}
            <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setFile(null)} disabled={!!progress}>Choose another file</button>
              <div className="arc-spacer" />
              {progress && <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>{formatNumber(progress.sent)} of {formatNumber(progress.total)}…</span>}
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!hasPhone || !!progress} onClick={() => void run()}>
                Import {formatNumber(file.rows.length)}
              </button>
            </div>
          </>
        )}
        {error && <div className="arc-banner arc-banner-warn" style={{ marginTop: 10 }}><Icon name="warning" size={14} /><span>{error}</span></div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------ API access

/**
 * What feeds this list by itself (docs/38): API clients, each a client id and a secret shown once.
 *
 * Server to server. The secret never travels -- the caller proves it with SCRAM-SHA-256 for a
 * fifteen-minute token -- and a browser cannot keep it, so a web form posts to the client's own
 * site and that calls here; a Facebook lead ad arrives through the client's CRM or a Zapier Code
 * step. The snippet below is the whole of what either needs.
 */
function Sources({ tenantId, list }: { tenantId: string; list: DiallerList }) {
  const can = useSession((s) => s.can);
  const clients = useDiallerApiClients(tenantId);
  const create = useCreateApiClient(tenantId);
  const update = useUpdateApiClient(tenantId);
  const revoke = useRevokeApiClient(tenantId);
  const step = useStepUp();
  const [name, setName] = useState('');
  const [made, setMade] = useState<{ id: string; name: string; secret: string } | null>(null);
  const mine = (clients.data?.data ?? []).filter((c) => c.list_id === list.id);

  return (
    <>
      {made && <SecretShown made={made} list={list} onClose={() => setMade(null)} />}

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Client</th><th>Client id</th><th className="arc-td-right">Leads</th><th>Last used</th><th>On</th><th /></tr></thead>
            {clients.data === undefined ? <TableSkeleton cols={6} /> : (
              <tbody>
                {mine.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{c.name}</span>
                      <div className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>secret …{c.secret_hint}</div>
                    </td>
                    <td className="arc-mono" style={{ fontSize: 12 }}>{c.id}</td>
                    <td className="arc-td-right arc-num">{formatNumber(c.uses)}</td>
                    <td className="arc-num">{c.last_used_at ? new Date(c.last_used_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</td>
                    <td>
                      <Switch id={`ac-${c.id}`} checked={c.enabled} disabled={!can('dialler:leadsource:write') || update.isPending}
                        onChange={(v) => update.mutate({ id: c.id, enabled: v })} />
                    </td>
                    <td className="arc-td-right">
                      {can('dialler:leadsource:write') && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { void confirmAction({ title: `Revoke ${c.name}?`, body: 'Its tokens stop working at once.', action: 'Revoke', danger: true }).then((ok) => { if (ok) void step.run(() => revoke.mutateAsync(c.id)); }); }}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {clients.data && mine.length === 0 && (
          <Empty icon="external" title="Nothing connected" hint="An API client lets your CRM, website or a Zapier step add people to this list." />
        )}
      </div>

      {can('dialler:leadsource:write') && (
        <form className="arc-card arc-card-body" onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void step.run(() => create.mutateAsync({ name: name.trim(), list_id: list.id }))
            .then((r) => { if (r) { setMade({ id: r.id, name: r.name, secret: r.secret }); setName(''); } });
        }}>
          <SubHead>New API client</SubHead>
          <div className="arc-field" style={{ margin: 0, maxWidth: 420 }}>
            <label className="arc-label" htmlFor="ac-name">Name</label>
            <input id="ac-name" className="arc-input" value={name} maxLength={120} required
              onChange={(e) => setName(e.target.value)} placeholder="Website quote form" />
          </div>
          <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" style={{ marginTop: 12 }}
            disabled={create.isPending || step.busy || !name.trim()}>Make the client</button>
          {create.error && <ErrorBanner error={create.error} />}
        </form>
      )}
      {(update.error || revoke.error) && <ErrorBanner error={update.error ?? revoke.error} />}
      <StepUpPrompt step={step} what="An API client">
        An API client lets whoever holds its secret add people to this list to be called.
      </StepUpPrompt>
    </>
  );
}

/** The id and secret, the secret this once, and the code that uses them. */
function SecretShown({ made, list, onClose }: {
  made: { id: string; name: string; secret: string }; list: DiallerList; onClose: () => void;
}) {
  const [how, setHow] = useState<'node' | 'zapier'>('node');
  const base = `${window.location.origin}/api/v1`;
  const code = leadSnippet(how, { base, clientId: made.id, fields: list.fields.map((f) => f.key) });
  const copy = (s: string) => void navigator.clipboard.writeText(s);

  return (
    <div className="arc-card arc-card-body" style={{ marginBottom: 14, borderLeft: '3px solid var(--arc-ok)' }}>
      <div className="arc-row" style={{ gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>{made.name}</strong>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onClose}>Done</button>
      </div>
      <p className="arc-muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        The secret is shown this once and is never sent over the wire: it signs in with SCRAM-SHA-256.
        A lost secret is replaced, not recovered.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '6px 10px', alignItems: 'center' }}>
        <span className="arc-label" style={{ margin: 0 }}>Client id</span>
        <code className="arc-mono" style={{ fontSize: 12, wordBreak: 'break-all' }}>{made.id}</code>
        <button type="button" className="arc-btn arc-btn-sm" onClick={() => copy(made.id)}>Copy</button>
        <span className="arc-label" style={{ margin: 0 }}>Secret</span>
        <code className="arc-mono" style={{ fontSize: 12, wordBreak: 'break-all' }}>{made.secret}</code>
        <button type="button" className="arc-btn arc-btn-sm" onClick={() => copy(made.secret)}>Copy</button>
      </div>

      <Tabs style={{ margin: '14px 0 10px' }}>
        <Tab active={how === 'node'} onSelect={() => setHow('node')}>Your server (Node)</Tab>
        <Tab active={how === 'zapier'} onSelect={() => setHow('zapier')}>Zapier (Code step)</Tab>
      </Tabs>
      {how === 'zapier' && (
        <p className="arc-hint" style={{ marginTop: 0 }}>
          Trigger on the lead (for example Facebook Lead Ads, New Lead), then add <strong>Code by Zapier, Run JavaScript</strong> with
          input data <code className="arc-mono">phone</code>, <code className="arc-mono">name</code>, <code className="arc-mono">email</code>
          {list.fields.length > 0 && <> and {list.fields.map((f) => f.key).join(', ')}</>}, and the secret as <code className="arc-mono">secret</code>.
        </p>
      )}
      <div className="arc-row" style={{ justifyContent: 'flex-end', marginBottom: 4 }}>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => copy(code)}>Copy code</button>
      </div>
      <pre className="arc-mono" style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', margin: 0, maxHeight: 360, overflow: 'auto' }}>{code}</pre>
      <p className="arc-hint" style={{ marginTop: 8 }}>
        Send the page, address and browser the person used as <code className="arc-mono">page</code>, <code className="arc-mono">consent_ip</code> and <code className="arc-mono">user_agent</code>:
        they are the record that the person asked to be called.
      </p>
    </div>
  );
}
